import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  versionLabel: string;
  frame: number;
  /** Source clip's first frame number, added to the displayed frame only. */
  startFrame?: number;
  hasDrawing: boolean;
  onCancel: () => void;
  onConfirm: (comment: string) => Promise<void>;
}

/**
 * Rejecting requires a reason. The dialog enforces it rather than the caller, so there is no
 * path through the UI that produces a bare "rejected" with nothing to act on.
 */
export function RejectDialog({
  open,
  versionLabel,
  frame,
  startFrame = 0,
  hasDrawing,
  onCancel,
  onConfirm,
}: Props): React.JSX.Element | null {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setText('');
      setBusy(false);
      // Focus after paint so the reviewer can just start typing.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const valid = text.trim().length > 0;

  const confirm = async (): Promise<void> => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onConfirm(text.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    // Cancel on click, not pointerdown: pressing down and dragging (selecting text, say)
    // should not dismiss a dialog that is holding unsaved text.
    <div className="modal" onClick={onCancel}>
      <div
        className="modal__box"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <h2>Reject {versionLabel}</h2>
        <p className="modal__hint">
          A rejection needs a reason. The note is pinned to frame {startFrame + frame}
          {hasDrawing ? ' — the current drawing is saved with it.' : '.'}
        </p>
        <textarea
          ref={inputRef}
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What needs to change?"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void confirm();
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="danger" onClick={() => void confirm()} disabled={!valid || busy}>
            {busy ? 'saving…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}
